import { useRouterState } from '@tanstack/react-router'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { createChatApi } from 'features/chatbot/api/chatApi'
import { useChatbotV2 } from 'features/chatbot/contexts/ChatbotSessionContext'
import { useChatbotContext } from 'features/chatbot/hooks/useChatbotContext'
import type { ChatSession, MessageStatus } from 'features/chatbot/types/chat'
import { useGeckoPermissions } from 'features/permissions'
import { useRolodexEntries } from 'features/rolodex'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AgentContextData,
  GeckoAgentContext,
  RolodexEntryType,
  RolodexFilterOp,
  SerializedConversationMessage,
  UnixTime,
} from 'types'

const HISTORY_LIMIT = 50

export interface ChatHistoryItem {
  entryId: string
  name: string | undefined
  updatedAt: UnixTime
  messageCount: number
  preview: string | undefined
  sessionId?: string
  chatLog?: SerializedConversationMessage[]
  lastMessageStatus?: MessageStatus | null
  activeStreamId?: string | null
  persistedThroughSeq?: number | null
}

function truncatePreview(text: string | null | undefined): string | undefined {
  if (!text) return undefined
  const trimmed = text.trim()
  if (trimmed.length === 0) return undefined
  return trimmed.length > 60 ? `${trimmed.slice(0, 60)}...` : trimmed
}

function useV1ChatHistory(enabled: boolean): {
  sessions: ChatHistoryItem[]
  isLoading: boolean
  refresh: () => void
} {
  const { user } = useGeckoPermissions()
  const userEmail = user?.email

  const query = useMemo(() => {
    if (!enabled || !userEmail) return undefined
    return {
      type: RolodexEntryType.CHAT_SESSION,
      filters: [
        {
          field: 'data',
          key: 'userId',
          op: RolodexFilterOp.EQUAL,
          value: userEmail,
        },
      ],
      order_by: [{ key: 'data.updatedAt', descending: 'true' }],
      limit: HISTORY_LIMIT,
    }
  }, [enabled, userEmail])

  const { entries, isLoading, forceRefresh } = useRolodexEntries(query, {
    allowCache: false,
  })

  const sessions = useMemo<ChatHistoryItem[]>(() => {
    if (!entries) return []
    return entries
      .map((entry) => {
        const agentContext = GeckoAgentContext.CreateFromJSON(entry.raw)
        const data = agentContext.data as AgentContextData
        const chatLog = data.chatLog ?? []
        const visibleMessages = chatLog.filter(
          (m) => m.type === 'userMessage' || m.type === 'aiResponse'
        )
        const firstUserMessage = chatLog.find((m) => m.type === 'userMessage')
        return {
          entryId: entry.id,
          sessionId: data.sessionId,
          name: data.name,
          updatedAt: data.updatedAt,
          messageCount: visibleMessages.length,
          chatLog,
          preview: truncatePreview(firstUserMessage?.text),
        }
      })
      .filter((s) => s.messageCount > 0)
  }, [entries])

  return { sessions, isLoading, refresh: forceRefresh }
}

function useV2ChatHistory(enabled: boolean): {
  sessions: ChatHistoryItem[]
  isLoading: boolean
  refresh: () => void
} {
  const { authorization } = useGeckoAuth()
  const { LLM_SERVICE } = useRuntimeVariables()
  const { user } = useGeckoPermissions()
  const userId = user?.id

  const routerState = useRouterState()
  const search = routerState.location.search
  const contextInfo = useChatbotContext(search)
  const orgId = contextInfo.organizationIds?.[0]
  const siteId = contextInfo.siteId

  const api = useMemo(
    () => createChatApi(LLM_SERVICE, authorization ?? ''),
    [LLM_SERVICE, authorization]
  )

  const [sessions, setSessions] = useState<ChatHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const inFlightRef = useRef(false)

  const canFetch = Boolean(
    enabled && userId && orgId && siteId && authorization
  )

  const refresh = useCallback(async () => {
    if (!canFetch || inFlightRef.current) return
    inFlightRef.current = true
    setIsLoading(true)
    try {
      const rows = await api.listSessions({
        orgId: orgId!,
        siteId: siteId!,
        userId: userId!,
        limit: HISTORY_LIMIT,
      })
      setSessions(
        rows
          .filter((row) => row.messageCount > 0 || row.preview)
          .map(
            (row: ChatSession): ChatHistoryItem => ({
              entryId: row.id,
              name: row.title ?? undefined,
              updatedAt: row.updatedAt as number as UnixTime,
              messageCount: row.messageCount,
              preview: truncatePreview(row.preview),
              lastMessageStatus: row.lastMessageStatus ?? null,
              activeStreamId: row.activeStreamId ?? null,
              persistedThroughSeq: row.persistedThroughSeq ?? null,
            })
          )
      )
    } catch (err) {
      console.warn('[useChatHistory] listSessions failed', err)
    } finally {
      inFlightRef.current = false
      setIsLoading(false)
    }
  }, [api, canFetch, orgId, siteId, userId])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { sessions, isLoading, refresh }
}

export function useChatHistory() {
  const useV2 = useChatbotV2()
  const v1 = useV1ChatHistory(!useV2)
  const v2 = useV2ChatHistory(useV2)
  return useV2 ? v2 : v1
}
